import { z } from 'zod';
import { CameraAliasId, UtcInstant } from './primitives.ts';

export const GameId = z.enum(['traffic-pulse', 'lane-duel', 'next-class', 'cycle-count', 'harbor-count']);
export type GameId = z.infer<typeof GameId>;

/**
 * How the pictures on screen relate to reality. These are never interchangeable labels.
 * SIMULATION: no video inference; events come from fixtures/synthetic generators.
 */
export const SourceMode = z.enum(['LIVE', 'DELAYED_LIVE', 'RECORDED_DEMO', 'SIMULATION']);
export type SourceMode = z.infer<typeof SourceMode>;

/** Readiness of a source for use. Anything other than APPROVED keeps real money disabled. */
export const SourceReadiness = z.enum([
  'APPROVED',
  'DEMO_ONLY',
  'RIGHTS_PENDING',
  'TIMING_UNTRUSTED',
  'UNSUPPORTED_SNAPSHOT',
  'QUARANTINED',
  /** Excluded on terms/rights grounds (e.g. frames only obtainable outside an official player). */
  'REJECTED',
]);
export type SourceReadiness = z.infer<typeof SourceReadiness>;

export const CameraHealthStatus = z.enum(['ONLINE', 'DEGRADED', 'OFFLINE', 'QUARANTINED']);

export const CameraHealth = z.object({
  cameraAlias: CameraAliasId,
  status: CameraHealthStatus,
  sourceMode: SourceMode,
  readiness: SourceReadiness,
  measuredFps: z.number().nonnegative().nullable(),
  /** Age of the newest frame relative to server now, when measurable. */
  sourceAgeMs: z.number().int().nonnegative().nullable(),
  /** Bound on capture clock error; null means unknown and must be labelled as such. */
  clockErrorBoundMs: z.number().int().nonnegative().nullable(),
  cvLagMs: z.number().int().nonnegative().nullable(),
  frameGapsLastMinute: z.number().int().nonnegative(),
  asOf: UtcInstant,
});
export type CameraHealth = z.infer<typeof CameraHealth>;

export const GameReadiness = z.enum(['PLAYABLE_DEMO', 'SIMULATION_ONLY', 'BLOCKED']);

export const GameDefinition = z.object({
  gameId: GameId,
  title: z.string().min(1).max(64),
  rulesVersion: z.string().min(1).max(32),
  mechanic: z.enum(['TOTAL_COUNT_BANDS', 'DIRECTION_DUEL', 'FIRST_CLASS', 'CYCLE_COUNT_BANDS', 'BOAT_COUNT_BANDS']),
  readiness: GameReadiness,
  realMoneyEnabled: z.boolean(),
  requiredSourceCapability: z.enum(['VIDEO_FIXED_ROAD', 'VIDEO_TWO_DIRECTIONS', 'VIDEO_CLASSIFIABLE', 'VIDEO_BIKE_LANE', 'VIDEO_HARBOR']),
});
export type GameDefinition = z.infer<typeof GameDefinition>;
